import { memo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Dialog } from "@mui/material";

// https://material-ui.com/components/dialogs/
const Modal = memo(
  ({ renderTrigger, renderContent, children, startOpen, onClose, disableOutsideClick, TransitionProps, ...rest }) => {
    const [isOpen, setIsOpen] = useState(startOpen);
    const dialogRef = useRef(null);

    function openModal() {
      setIsOpen(true);
    }

    function closeModal() {
      setIsOpen(false);
    }

    const stopPropagationForTab = (event) => {
      if (event.key === "Tab") {
        event.stopPropagation();
      }
    };

    const handleDialogEntered = (...args) => {
      // Call existing onEntered callback if provided
      if (TransitionProps?.onEntered) {
        TransitionProps.onEntered(...args);
        // If a custom callback is provided, let it handle focus management
        return;
      }

      // Only auto-focus if no custom callback is provided
      // Move focus to first focusable element in dialog when it opens
      if (dialogRef.current) {
        const focusableElements = dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    };

    return (
      <div>
        {renderTrigger({ openModal })}
        <Dialog
          ref={dialogRef}
          onClose={(event, reason) => {
            if (disableOutsideClick && reason && reason === "backdropClick")
              return;

            if (typeof onClose == "function") onClose();
            setIsOpen(false);
          }}
          open={isOpen}
          fullWidth
          maxWidth="sm"
          onKeyDown={stopPropagationForTab}
          TransitionProps={{
            ...TransitionProps,
            onEntered: handleDialogEntered
          }}
          {...rest}
        >
          {renderContent && renderContent({ closeModal })}
          {children}
        </Dialog>
      </div>
    );
  }
);

export default Modal;

Modal.propTypes = {
  renderTrigger: PropTypes.func.isRequired,
  renderContent: PropTypes.func,
  children: PropTypes.node,
  startOpen: PropTypes.bool,
  onClose: PropTypes.func,
  disableOutsideClick: PropTypes.bool,
  TransitionProps: PropTypes.object,
};
Modal.defaultProps = {
  startOpen: false,
};
